import * as copy from 'src/ui/templates/copy';

export type SduUsageRow = {
  date: string;
  units: number;
  remarks?: string | null;
};

export const roundSduUnits = (units: number) => Math.round(units * 100) / 100;

export const formatSduUnits = (units: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
    roundSduUnits(units)
  );

const escapeCsv = (cell: string) => {
  // OWASP CSV injection: neutralize =, +, -, @. Spreadsheets trim leading
  // whitespace first; tab and CR in that prefix are themselves formula-start.
  const trimmed = cell.replace(/^\s+/, '');
  const leading = cell.slice(0, cell.length - trimmed.length);
  const needsFormulaPrefix = /^[=+\-@]/.test(trimmed) || /[\t\r]/.test(leading);
  const safe = needsFormulaPrefix ? `'${cell}` : cell;
  // RFC 4180: quote if the field contains comma, CR, LF, or quote.
  // Also quote neutralized formula cells so the leading apostrophe is preserved.
  if (needsFormulaPrefix || /[,\n\r"]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
};

/**
 * Builds the Stardog Unit usage CSV: one row per day followed by a TOTAL row,
 * with unit values rounded to two decimal places. Rows are written in the order
 * given, so callers pass them date-ascending.
 */
export function buildSduUsageCsv(
  rows: SduUsageRow[],
  endpoint: { name: string; url: string }
): string {
  const headerRow = copy.components.stardogUnitUsage.csvHeaders.join(',');
  const name = escapeCsv(endpoint.name);
  const url = escapeCsv(endpoint.url);

  let total = 0;
  const dataRows = rows.map(({ date, units, remarks }) => {
    const dailyTotal = roundSduUnits(units);
    total += dailyTotal;
    return [
      escapeCsv(date),
      name,
      url,
      dailyTotal,
      escapeCsv(remarks || ''),
    ].join(',');
  });
  const totalRow = ['TOTAL', '', '', roundSduUnits(total), ''].join(',');

  return [headerRow, ...dataRows, totalRow].join('\n');
}
